import { Plus, X } from "lucide-react";
import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
	DEFAULT_SWIMLANE_ID,
	assignmentColumn,
	assignmentPosition,
	assignmentSwimlane,
	createCustomSwimlane,
	moveSwimlaneAssignments,
	normalizeCustomSwimlanes,
	reorderSwimlanes,
} from "../../swimlaneLanes";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { SwimlaneCard } from "../SwimlaneCard";
import { SwimlaneHeader } from "../SwimlaneHeader";

const COLUMN_DEFINITIONS = [
	{ id: "new", title: "New", color: "bg-blue-500" },
	{ id: "toReview", title: "To Review", color: "bg-orange-500" },
	{
		id: "waitingOnAuthor",
		title: "Waiting on the Author",
		color: "bg-yellow-500",
	},
	{ id: "reviewed", title: "Reviewed", color: "bg-green-500" },
	{ id: "tracking", title: "Tracking", color: "bg-gray-500" },
	{ id: "merged", title: "Merged", color: "bg-purple-500" },
	{ id: "deleted", title: "Deleted", color: "bg-red-500" },
];

const COLUMN_IDS = new Set(COLUMN_DEFINITIONS.map((column) => column.id));
const FLAT_SWIMLANE = { id: "all", title: "All merge requests" };

const createSwimlaneId = () => {
	if (window.crypto?.randomUUID) {
		return `workstream-${window.crypto.randomUUID()}`;
	}
	return `workstream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const defaultColumnFor = (mr) => (mr.state === "merged" ? "merged" : "new");

const assignmentMetadata = (assignment) =>
	typeof assignment === "object" && assignment !== null ? assignment : {};

export const effectivePlacement = (
	mr,
	assignment,
	customSwimlaneIds,
	validSwimlaneIds,
) => {
	let column = assignmentColumn(assignment) || defaultColumnFor(mr);
	let swimlane = assignmentSwimlane(assignment);
	if (
		customSwimlaneIds.has(column) &&
		!assignmentMetadata(assignment).swimlane
	) {
		swimlane = column;
		column = "toReview";
	}
	if (column === "hidden") {
		return {
			column,
			swimlane: validSwimlaneIds.has(swimlane) ? swimlane : DEFAULT_SWIMLANE_ID,
		};
	}
	const lastSwimlane = assignmentMetadata(assignment).lastSwimlane;
	if (column === "new" && validSwimlaneIds.has(lastSwimlane)) {
		swimlane = lastSwimlane;
	}
	if (mr.state === "merged") column = "merged";
	else if (column === "new" && mr.state === "closed") column = "deleted";
	else if (!COLUMN_IDS.has(column) && column !== "hidden") column = "new";
	if (column === "new") swimlane = DEFAULT_SWIMLANE_ID;
	if (!validSwimlaneIds.has(swimlane)) swimlane = DEFAULT_SWIMLANE_ID;
	return { column, swimlane };
};

const getCellItems = (
	assignments,
	columnId,
	swimlaneId,
	allMRs,
	customSwimlaneIds,
	validSwimlaneIds,
) =>
	allMRs
		.map((mr) => {
			const assignment = assignments[mr.id];
			return {
				id: mr.id,
				position: assignmentPosition(assignment),
				placement: effectivePlacement(
					mr,
					assignment,
					customSwimlaneIds,
					validSwimlaneIds,
				),
			};
		})
		.filter(
			(item) =>
				item.placement.column === columnId &&
				item.placement.swimlane === swimlaneId,
		)
		.sort((first, second) => first.position - second.position);

const getMaxEffectivePosition = (
	assignments,
	columnId,
	swimlaneId,
	mrById,
	customSwimlaneIds,
	validSwimlaneIds,
) =>
	Object.entries(assignments).reduce((max, [mrId, assignment]) => {
		const mr = mrById.get(mrId);
		const placement = mr
			? effectivePlacement(mr, assignment, customSwimlaneIds, validSwimlaneIds)
			: {
					column: assignmentColumn(assignment),
					swimlane: assignmentSwimlane(assignment),
				};
		if (placement.column !== columnId || placement.swimlane !== swimlaneId) {
			return max;
		}
		return Math.max(max, assignmentPosition(assignment));
	}, -1);

export const SwimlaneView = ({
	pendingReviewMRs,
	myOpenMRs,
	recentlyClosedMRs,
	sourcePendingReviewMRs = pendingReviewMRs,
	sourceMyOpenMRs = myOpenMRs,
	sourceRecentlyClosedMRs = recentlyClosedMRs,
	loading,
	error,
	showMergedColumn = true,
	showDeletedColumn = false,
	workstreamsEnabled = true,
	hasIncompleteResults = false,
	hideCreatedDaysThreshold = 30,
	hideUpdatedDaysThreshold = 14,
	enableCommentsExpansion = false,
}) => {
	const [draggedMR, setDraggedMR] = useState(null);
	const [hoverPosition, setHoverPosition] = useState(null);
	const [laneAssignments, setLaneAssignments] = useLocalStorage(
		"swimlaneLaneAssignments",
		{},
	);
	const [storedCustomSwimlanes, setStoredCustomSwimlanes] = useLocalStorage(
		"swimlaneCustomLanes",
		[],
	);
	const [collapsedSwimlaneIds, setCollapsedSwimlaneIds] = useLocalStorage(
		"swimlaneCollapsedRows",
		[],
	);
	const [storedSwimlaneOrder, setStoredSwimlaneOrder] = useLocalStorage(
		"swimlaneRowOrder",
		[],
	);
	const [draggedSwimlaneId, setDraggedSwimlaneId] = useState(null);
	const [swimlaneDropTarget, setSwimlaneDropTarget] = useState(null);
	const draggedSwimlaneIdRef = useRef(null);
	const [isAddingSwimlane, setIsAddingSwimlane] = useState(false);
	const [newSwimlaneName, setNewSwimlaneName] = useState("");
	const [swimlanePendingDelete, setSwimlanePendingDelete] = useState(null);
	const newSwimlaneInputRef = useRef(null);

	const customSwimlanes = useMemo(
		() => normalizeCustomSwimlanes(storedCustomSwimlanes),
		[storedCustomSwimlanes],
	);
	const swimlanes = useMemo(() => {
		const available = [
			{
				id: DEFAULT_SWIMLANE_ID,
				title: "Anything Else",
				isCustom: false,
			},
			...customSwimlanes.map((swimlane) => ({
				...swimlane,
				isCustom: true,
			})),
		];
		const byId = new Map(available.map((swimlane) => [swimlane.id, swimlane]));
		const storedOrder = Array.isArray(storedSwimlaneOrder)
			? storedSwimlaneOrder
			: [];
		const seenIds = new Set();
		const ordered = [];
		for (const id of storedOrder) {
			const swimlane = byId.get(id);
			if (swimlane && !seenIds.has(id)) {
				ordered.push(swimlane);
				seenIds.add(id);
			}
		}
		const orderedIds = new Set(ordered.map((swimlane) => swimlane.id));
		return [
			...ordered,
			...available.filter((swimlane) => !orderedIds.has(swimlane.id)),
		];
	}, [customSwimlanes, storedSwimlaneOrder]);
	useEffect(() => {
		const canonicalOrder = swimlanes.map((swimlane) => swimlane.id);
		setStoredSwimlaneOrder((storedOrder) => {
			const currentOrder = Array.isArray(storedOrder) ? storedOrder : [];
			const isCurrent =
				currentOrder.length === canonicalOrder.length &&
				currentOrder.every((id, index) => id === canonicalOrder[index]);
			return isCurrent ? storedOrder : canonicalOrder;
		});
	}, [swimlanes, setStoredSwimlaneOrder]);
	const validSwimlaneIds = useMemo(
		() => new Set(swimlanes.map((swimlane) => swimlane.id)),
		[swimlanes],
	);
	const customSwimlaneIds = useMemo(
		() => new Set(customSwimlanes.map((swimlane) => swimlane.id)),
		[customSwimlanes],
	);
	const collapsedSwimlanes = useMemo(
		() =>
			new Set(Array.isArray(collapsedSwimlaneIds) ? collapsedSwimlaneIds : []),
		[collapsedSwimlaneIds],
	);
	const visibleColumns = useMemo(
		() =>
			COLUMN_DEFINITIONS.filter((column) => {
				if (column.id === "merged" && !showMergedColumn) return false;
				if (column.id === "deleted" && !showDeletedColumn) return false;
				return true;
			}),
		[showMergedColumn, showDeletedColumn],
	);
	const workstreamColumns = useMemo(
		() => visibleColumns.filter((column) => column.id !== "new"),
		[visibleColumns],
	);
	const allMRs = useMemo(() => {
		const byId = new Map();
		for (const mr of [
			...pendingReviewMRs,
			...myOpenMRs,
			...recentlyClosedMRs,
		]) {
			byId.set(mr.id, mr);
		}
		return [...byId.values()];
	}, [pendingReviewMRs, myOpenMRs, recentlyClosedMRs]);
	const sourceMRs = useMemo(() => {
		const byId = new Map();
		for (const mr of [
			...sourcePendingReviewMRs,
			...sourceMyOpenMRs,
			...sourceRecentlyClosedMRs,
		]) {
			byId.set(mr.id, mr);
		}
		return [...byId.values()];
	}, [sourcePendingReviewMRs, sourceMyOpenMRs, sourceRecentlyClosedMRs]);

	useEffect(() => {
		if (isAddingSwimlane) newSwimlaneInputRef.current?.focus();
	}, [isAddingSwimlane]);
	useEffect(() => {
		if (!workstreamsEnabled) {
			setIsAddingSwimlane(false);
			setNewSwimlaneName("");
		}
	}, [workstreamsEnabled]);

	useEffect(() => {
		if (sourceMRs.length === 0 || hasIncompleteResults) return;

		const mrById = new Map(sourceMRs.map((mr) => [mr.id, mr]));
		const effectiveUpdatedThreshold = Math.min(
			hideUpdatedDaysThreshold,
			hideCreatedDaysThreshold,
		);
		const now = new Date();
		const createdThreshold = new Date(now);
		createdThreshold.setDate(
			createdThreshold.getDate() - hideCreatedDaysThreshold,
		);
		const updatedThreshold = new Date(now);
		updatedThreshold.setDate(
			updatedThreshold.getDate() - effectiveUpdatedThreshold,
		);

		setLaneAssignments((previousAssignments) => {
			const assignments = { ...previousAssignments };
			let changed = false;

			for (const [mrId, existingAssignment] of Object.entries(assignments)) {
				const metadata = assignmentMetadata(existingAssignment);
				let column = assignmentColumn(existingAssignment) || "new";
				let swimlane = assignmentSwimlane(existingAssignment);

				// Migrate the short-lived custom-column implementation to real rows.
				if (customSwimlaneIds.has(column) && !metadata.swimlane) {
					swimlane = column;
					column = "toReview";
				}
				if (!validSwimlaneIds.has(swimlane)) swimlane = DEFAULT_SWIMLANE_ID;
				if (!COLUMN_IDS.has(column) && column !== "hidden") column = "new";

				const normalized = {
					...metadata,
					lane: column,
					swimlane,
					position: assignmentPosition(existingAssignment),
				};
				if (
					typeof existingAssignment !== "object" ||
					existingAssignment === null ||
					existingAssignment.lane !== normalized.lane ||
					existingAssignment.swimlane !== normalized.swimlane ||
					existingAssignment.position !== normalized.position
				) {
					assignments[mrId] = normalized;
					changed = true;
				}
			}

			for (const mr of sourceMRs) {
				let existing = assignments[mr.id];
				if (!existing) {
					const { column, swimlane } = effectivePlacement(
						mr,
						undefined,
						customSwimlaneIds,
						validSwimlaneIds,
					);
					assignments[mr.id] = {
						lane: column,
						swimlane,
						position:
							getMaxEffectivePosition(
								assignments,
								column,
								swimlane,
								mrById,
								customSwimlaneIds,
								validSwimlaneIds,
							) + 1,
						createdAt: mr.createdAt,
						updatedAt: mr.updatedAt,
					};
					changed = true;
					continue;
				}

				const createdAt = existing.createdAt || mr.createdAt;
				const updatedAt = mr.updatedAt || existing.updatedAt;
				if (
					existing.createdAt !== createdAt ||
					existing.updatedAt !== updatedAt
				) {
					existing = { ...existing, createdAt, updatedAt };
					assignments[mr.id] = existing;
					changed = true;
				}
			}

			for (const [mrId, assignment] of Object.entries(assignments)) {
				const createdAt = assignment.createdAt;
				const updatedAt = assignment.updatedAt;
				const isStale =
					createdAt &&
					updatedAt &&
					new Date(createdAt) < createdThreshold &&
					new Date(updatedAt) < updatedThreshold;

				if (isStale && assignment.lane !== "hidden") {
					assignments[mrId] = {
						...assignment,
						lane: "hidden",
						hiddenAt: now.toISOString(),
					};
					changed = true;
					continue;
				}

				if (assignment.lane === "hidden" && !isStale) {
					const mr = mrById.get(mrId);
					if (mr) {
						const defaultColumn = defaultColumnFor(mr);
						const column =
							defaultColumn === "new" && mr.state === "closed"
								? "deleted"
								: defaultColumn;
						const swimlane =
							defaultColumn === "new"
								? DEFAULT_SWIMLANE_ID
								: assignmentSwimlane(assignment);
						const visibleAssignment = { ...assignment };
						Reflect.deleteProperty(visibleAssignment, "hiddenAt");
						assignments[mrId] = {
							...visibleAssignment,
							lane: column,
							swimlane,
							position:
								getMaxEffectivePosition(
									assignments,
									column,
									swimlane,
									mrById,
									customSwimlaneIds,
									validSwimlaneIds,
								) + 1,
						};
						changed = true;
					}
				} else if (assignment.lane === "hidden" && !assignment.hiddenAt) {
					assignments[mrId] = {
						...assignment,
						hiddenAt: now.toISOString(),
					};
					changed = true;
				}
			}

			const oneMonthAgo = new Date(now);
			oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
			for (const [mrId, assignment] of Object.entries(assignments)) {
				if (
					assignment.lane === "hidden" &&
					assignment.hiddenAt &&
					new Date(assignment.hiddenAt) < oneMonthAgo
				) {
					delete assignments[mrId];
					changed = true;
				}
			}

			return changed ? assignments : previousAssignments;
		});
	}, [
		sourceMRs,
		customSwimlaneIds,
		validSwimlaneIds,
		hasIncompleteResults,
		hideCreatedDaysThreshold,
		hideUpdatedDaysThreshold,
		setLaneAssignments,
	]);

	const board = useMemo(() => {
		const categorized = {};
		for (const swimlane of swimlanes) {
			categorized[swimlane.id] = Object.fromEntries(
				COLUMN_DEFINITIONS.map((column) => [column.id, []]),
			);
		}

		for (const mr of allMRs) {
			const assignment = laneAssignments[mr.id];
			const { column, swimlane } = effectivePlacement(
				mr,
				assignment,
				customSwimlaneIds,
				validSwimlaneIds,
			);
			if (column === "hidden") continue;

			categorized[swimlane][column].push({
				mr,
				position: assignmentPosition(assignment),
			});
		}

		for (const swimlane of swimlanes) {
			for (const column of COLUMN_DEFINITIONS) {
				categorized[swimlane.id][column.id] = categorized[swimlane.id][
					column.id
				]
					.sort((first, second) => first.position - second.position)
					.map((item) => item.mr);
			}
		}
		return categorized;
	}, [allMRs, laneAssignments, swimlanes, customSwimlaneIds, validSwimlaneIds]);
	const flatBoard = useMemo(
		() =>
			Object.fromEntries(
				COLUMN_DEFINITIONS.map((column) => [
					column.id,
					swimlanes.flatMap((swimlane) => board[swimlane.id][column.id]),
				]),
			),
		[board, swimlanes],
	);
	const sourceBoard = useMemo(() => {
		const categorized = {};
		for (const swimlane of swimlanes) {
			categorized[swimlane.id] = Object.fromEntries(
				COLUMN_DEFINITIONS.map((column) => [column.id, []]),
			);
		}

		for (const mr of sourceMRs) {
			const assignment = laneAssignments[mr.id];
			const placement = effectivePlacement(
				mr,
				assignment,
				customSwimlaneIds,
				validSwimlaneIds,
			);
			if (placement.column === "hidden") continue;
			categorized[placement.swimlane][placement.column].push({
				id: mr.id,
				position: assignmentPosition(assignment),
				placement,
			});
		}

		for (const swimlane of swimlanes) {
			for (const column of COLUMN_DEFINITIONS) {
				categorized[swimlane.id][column.id].sort(
					(first, second) => first.position - second.position,
				);
			}
		}
		return categorized;
	}, [
		sourceMRs,
		laneAssignments,
		swimlanes,
		customSwimlaneIds,
		validSwimlaneIds,
	]);
	const sourceCellItems = (columnId, swimlaneId) =>
		sourceBoard[swimlaneId]?.[columnId] || [];

	const handleAddSwimlane = (event) => {
		event.preventDefault();
		const swimlane = createCustomSwimlane(newSwimlaneName, createSwimlaneId());
		if (!swimlane) return;
		setStoredCustomSwimlanes((existing) => [
			...normalizeCustomSwimlanes(existing),
			swimlane,
		]);
		setNewSwimlaneName("");
		setIsAddingSwimlane(false);
	};

	const handleRenameSwimlane = (swimlaneId, title) => {
		setStoredCustomSwimlanes((existing) =>
			normalizeCustomSwimlanes(existing).map((swimlane) =>
				swimlane.id === swimlaneId ? { ...swimlane, title } : swimlane,
			),
		);
	};

	const requestDeleteSwimlane = (swimlaneId) => {
		const swimlane = customSwimlanes.find((item) => item.id === swimlaneId);
		if (!swimlane) return;
		setSwimlanePendingDelete(swimlane);
	};
	const cancelDeleteSwimlane = useCallback(
		() => setSwimlanePendingDelete(null),
		[],
	);

	const confirmDeleteSwimlane = () => {
		if (!swimlanePendingDelete) return;
		const swimlaneId = swimlanePendingDelete.id;
		setLaneAssignments((assignments) => {
			const normalizedAssignments = { ...assignments };
			for (const mr of sourceMRs) {
				const assignment = assignments[mr.id];
				if (!assignment) continue;
				const placement = effectivePlacement(
					mr,
					assignment,
					customSwimlaneIds,
					validSwimlaneIds,
				);
				normalizedAssignments[mr.id] = {
					...assignmentMetadata(assignment),
					lane: placement.column,
					swimlane: placement.swimlane,
					position: assignmentPosition(assignment),
				};
			}
			return moveSwimlaneAssignments(normalizedAssignments, swimlaneId);
		});
		setStoredCustomSwimlanes((existing) =>
			normalizeCustomSwimlanes(existing).filter(
				(item) => item.id !== swimlaneId,
			),
		);
		setCollapsedSwimlaneIds((existing) =>
			Array.isArray(existing)
				? existing.filter((item) => item !== swimlaneId)
				: [],
		);
		setStoredSwimlaneOrder((existing) =>
			Array.isArray(existing)
				? existing.filter((item) => item !== swimlaneId)
				: [],
		);
		setSwimlanePendingDelete(null);
	};

	const handleToggleSwimlane = (swimlaneId) => {
		setCollapsedSwimlaneIds((existing) => {
			const ids = new Set(Array.isArray(existing) ? existing : []);
			if (ids.has(swimlaneId)) ids.delete(swimlaneId);
			else ids.add(swimlaneId);
			return [...ids];
		});
	};

	const handleSwimlaneReorderStart = (swimlaneId, event) => {
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", `swimlane:${swimlaneId}`);
		event.dataTransfer.setData("application/x-swimlane-id", swimlaneId);
		draggedSwimlaneIdRef.current = swimlaneId;
		setDraggedSwimlaneId(swimlaneId);
		setDraggedMR(null);
		setHoverPosition(null);
	};

	const handleSwimlaneReorderEnd = () => {
		draggedSwimlaneIdRef.current = null;
		setDraggedSwimlaneId(null);
		setSwimlaneDropTarget(null);
	};

	const moveCard = useCallback(
		(mrId, columnId, swimlaneId, position) => {
			const targetSwimlaneId =
				columnId === "new" ? DEFAULT_SWIMLANE_ID : swimlaneId;
			setLaneAssignments((assignments) => {
				const existing = assignmentMetadata(assignments[mrId]);
				const targetItems = getCellItems(
					assignments,
					columnId,
					targetSwimlaneId,
					sourceMRs,
					customSwimlaneIds,
					validSwimlaneIds,
				);
				const currentIndex = targetItems.findIndex((item) => item.id === mrId);
				if (
					currentIndex !== -1 &&
					(position === currentIndex || position === currentIndex + 1)
				) {
					return assignments;
				}

				const reordered = targetItems.filter((item) => item.id !== mrId);
				let adjustedPosition = position;
				if (currentIndex !== -1 && position > currentIndex) {
					adjustedPosition -= 1;
				}
				reordered.splice(adjustedPosition, 0, { id: mrId });

				const nextAssignments = { ...assignments };
				for (const [index, item] of reordered.entries()) {
					const itemMetadata = assignmentMetadata(assignments[item.id]);
					nextAssignments[item.id] = {
						...itemMetadata,
						lane: columnId,
						swimlane: targetSwimlaneId,
						position: index,
					};
				}
				nextAssignments[mrId] = {
					...nextAssignments[mrId],
					...existing,
					lane: columnId,
					swimlane: targetSwimlaneId,
					position: adjustedPosition,
				};
				if (
					columnId === "new" &&
					swimlaneId !== DEFAULT_SWIMLANE_ID &&
					validSwimlaneIds.has(swimlaneId)
				) {
					nextAssignments[mrId].lastSwimlane = swimlaneId;
				} else {
					Reflect.deleteProperty(nextAssignments[mrId], "lastSwimlane");
				}
				return nextAssignments;
			});
		},
		[setLaneAssignments, sourceMRs, customSwimlaneIds, validSwimlaneIds],
	);

	const handleDrop = (event, columnId, swimlaneId, position) => {
		event.preventDefault();
		if (!draggedMR) return;
		moveCard(draggedMR.id, columnId, swimlaneId, position);
		setDraggedMR(null);
		setHoverPosition(null);
	};

	const handleCellDragOver = (event, columnId, swimlaneId, itemCount) => {
		event.preventDefault();
		if (!draggedMR) return;
		const assignment = laneAssignments[draggedMR.id];
		const placement = effectivePlacement(
			draggedMR,
			assignment,
			customSwimlaneIds,
			validSwimlaneIds,
		);
		if (placement.column !== columnId || placement.swimlane !== swimlaneId) {
			setHoverPosition({
				column: columnId,
				swimlane: swimlaneId,
				position: itemCount,
			});
		}
	};

	const draggedCardIsNew = () => {
		if (!draggedMR) return false;
		return (
			effectivePlacement(
				draggedMR,
				laneAssignments[draggedMR.id],
				customSwimlaneIds,
				validSwimlaneIds,
			).column === "new"
		);
	};

	const getDraggedSwimlaneId = (event) => {
		if (draggedSwimlaneIdRef.current) return draggedSwimlaneIdRef.current;
		const typedId = event.dataTransfer?.getData("application/x-swimlane-id");
		if (typedId) return typedId;
		const text = event.dataTransfer?.getData("text/plain") || "";
		return text.startsWith("swimlane:") ? text.slice("swimlane:".length) : null;
	};

	const getSwimlaneReorderEdge = (movingId, targetId) => {
		const ids = swimlanes.map((swimlane) => swimlane.id);
		return ids.indexOf(movingId) < ids.indexOf(targetId) ? "after" : "before";
	};

	const handleSwimlaneReorderDragOver = (event, swimlaneId) => {
		const movingId = getDraggedSwimlaneId(event);
		if (!movingId || movingId === swimlaneId) return false;
		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		const edge = getSwimlaneReorderEdge(movingId, swimlaneId);
		setSwimlaneDropTarget({ id: swimlaneId, edge });
		return true;
	};

	const handleSwimlaneReorderDrop = (event, swimlaneId) => {
		const movingId = getDraggedSwimlaneId(event);
		if (!movingId || movingId === swimlaneId) return false;
		event.preventDefault();
		event.stopPropagation();
		const edge =
			swimlaneDropTarget?.id === swimlaneId
				? swimlaneDropTarget.edge
				: getSwimlaneReorderEdge(movingId, swimlaneId);
		setStoredSwimlaneOrder(
			reorderSwimlanes(
				swimlanes.map((swimlane) => swimlane.id),
				movingId,
				swimlaneId,
				edge,
			),
		);
		handleSwimlaneReorderEnd();
		return true;
	};

	const handleSwimlaneTitleDragOver = (event, swimlaneId) => {
		if (handleSwimlaneReorderDragOver(event, swimlaneId)) return;
		if (!draggedCardIsNew()) return;
		event.preventDefault();
		setHoverPosition({ header: swimlaneId });
	};

	const handleSwimlaneTitleDrop = (event, swimlaneId) => {
		if (handleSwimlaneReorderDrop(event, swimlaneId)) return;
		if (!draggedCardIsNew()) return;
		event.preventDefault();
		handleDrop(
			event,
			"toReview",
			swimlaneId,
			sourceCellItems("toReview", swimlaneId).length,
		);
	};

	const wouldMove = (columnId, swimlaneId, position) => {
		if (!draggedMR) return false;
		const items = sourceCellItems(columnId, swimlaneId);
		const currentIndex = items.findIndex((item) => item.id === draggedMR.id);
		return (
			currentIndex === -1 ||
			(position !== currentIndex && position !== currentIndex + 1)
		);
	};

	const handleMoveColumn = (mrId, columnId) => {
		const mr = sourceMRs.find((item) => item.id === mrId);
		const assignment = laneAssignments[mrId];
		const placement = mr
			? effectivePlacement(mr, assignment, customSwimlaneIds, validSwimlaneIds)
			: {
					column: assignmentColumn(assignment),
					swimlane: assignmentSwimlane(assignment),
				};
		const latentSwimlane = assignmentMetadata(assignment).lastSwimlane;
		const swimlaneId =
			placement.column === "new" && validSwimlaneIds.has(latentSwimlane)
				? latentSwimlane
				: placement.swimlane;
		const targetPositionSwimlane =
			columnId === "new" ? DEFAULT_SWIMLANE_ID : swimlaneId;
		moveCard(
			mrId,
			columnId,
			swimlaneId,
			sourceCellItems(columnId, targetPositionSwimlane).length,
		);
	};

	const handleMoveSwimlane = (mrId, swimlaneId) => {
		const mr = sourceMRs.find((item) => item.id === mrId);
		let columnId = mr
			? effectivePlacement(
					mr,
					laneAssignments[mrId],
					customSwimlaneIds,
					validSwimlaneIds,
				).column
			: assignmentColumn(laneAssignments[mrId]) || "new";
		if (columnId === "new") columnId = "toReview";
		moveCard(
			mrId,
			columnId,
			swimlaneId,
			sourceCellItems(columnId, swimlaneId).length,
		);
	};

	const handleDeleteItem = (mrId) => handleMoveColumn(mrId, "deleted");
	const handleRestoreItem = (mrId) => handleMoveColumn(mrId, "new");

	const renderColumnCell = (swimlane, column, providedMRs, isFlat = false) => {
		const mrs = providedMRs || board[swimlane.id][column.id];
		const draggedPlacement = draggedMR
			? effectivePlacement(
					draggedMR,
					laneAssignments[draggedMR.id],
					customSwimlaneIds,
					validSwimlaneIds,
				)
			: null;
		const draggedLastSwimlane = draggedMR
			? assignmentMetadata(laneAssignments[draggedMR.id]).lastSwimlane
			: null;
		const draggedPreferredSwimlane =
			draggedPlacement?.column === "new" &&
			validSwimlaneIds.has(draggedLastSwimlane)
				? draggedLastSwimlane
				: draggedPlacement?.swimlane || DEFAULT_SWIMLANE_ID;
		const targetSwimlaneId = isFlat ? draggedPreferredSwimlane : swimlane.id;
		const moveTargetSwimlaneId =
			column.id === "new" ? draggedPreferredSwimlane : targetSwimlaneId;
		const positionSwimlaneId =
			column.id === "new" ? DEFAULT_SWIMLANE_ID : targetSwimlaneId;
		const persistedItems = sourceCellItems(column.id, positionSwimlaneId);
		const persistedPositionAt = (visibleIndex) => {
			if (visibleIndex >= mrs.length) return persistedItems.length;
			const targetId = mrs[visibleIndex].id;
			const position = persistedItems.findIndex((item) => item.id === targetId);
			return position === -1 ? persistedItems.length : position;
		};
		const flatDropPosition = (() => {
			if (!isFlat || !draggedMR) return mrs.length;
			const visualTargetSwimlane =
				column.id === "new" ? DEFAULT_SWIMLANE_ID : targetSwimlaneId;
			let lastMatchingIndex = -1;
			for (const [index, mr] of mrs.entries()) {
				const placement = effectivePlacement(
					mr,
					laneAssignments[mr.id],
					customSwimlaneIds,
					validSwimlaneIds,
				);
				if (placement.swimlane === visualTargetSwimlane) {
					lastMatchingIndex = index;
				}
			}
			if (lastMatchingIndex !== -1) return lastMatchingIndex + 1;
			const targetOrder = swimlanes.findIndex(
				(swimlane) => swimlane.id === visualTargetSwimlane,
			);
			let boundary = 0;
			for (const [index, mr] of mrs.entries()) {
				const placement = effectivePlacement(
					mr,
					laneAssignments[mr.id],
					customSwimlaneIds,
					validSwimlaneIds,
				);
				const itemOrder = swimlanes.findIndex(
					(swimlane) => swimlane.id === placement.swimlane,
				);
				if (itemOrder < targetOrder) boundary = index + 1;
			}
			return boundary;
		})();
		const acceptsDraggedMR = draggedMR
			? draggedMR.state === "merged"
				? column.id === "merged"
				: column.id !== "merged" &&
					!(draggedMR.state === "closed" && column.id === "new")
			: column.id !== "merged";
		const isDragTarget =
			hoverPosition?.column === column.id &&
			hoverPosition?.swimlane === swimlane.id;
		const moveColumns = visibleColumns.filter(
			(item) =>
				item.id !== column.id && item.id !== "merged" && item.id !== "deleted",
		);
		const moveSwimlanes = isFlat
			? []
			: column.id === "new"
				? swimlanes
				: swimlanes.filter((item) => item.id !== swimlane.id);

		return (
			<ul
				key={`${swimlane.id}:${column.id}`}
				className={`min-h-32 min-w-0 flex-1 rounded-lg border bg-[var(--color-bg-primary)] p-3 shadow-sm transition-all ${
					isDragTarget
						? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]"
						: "border-[var(--color-border)]"
				}`}
				aria-label={`${swimlane.title}, ${column.title}`}
				onDragOver={
					acceptsDraggedMR
						? (event) => {
								if (isFlat && draggedMR) {
									event.preventDefault();
									setHoverPosition({
										column: column.id,
										swimlane: swimlane.id,
										position: flatDropPosition,
									});
								} else {
									handleCellDragOver(event, column.id, swimlane.id, mrs.length);
								}
							}
						: undefined
				}
				onDrop={
					acceptsDraggedMR
						? (event) =>
								handleDrop(
									event,
									column.id,
									moveTargetSwimlaneId,
									persistedItems.length,
								)
						: undefined
				}
			>
				{mrs.length === 0 && (
					<li className="list-none py-8 text-center text-sm text-[var(--color-text-secondary)]">
						No merge requests
					</li>
				)}
				{mrs.map((mr, index) => {
					const isBeingDragged = draggedMR?.id === mr.id;
					return (
						<Fragment key={mr.id}>
							{isFlat && isDragTarget && hoverPosition.position === index && (
								<li className="mx-2 mb-2 h-1 list-none rounded-full bg-[var(--color-accent)]" />
							)}
							<SwimlaneCard
								mr={mr}
								isDragging={!!draggedMR}
								showIndicator={
									!isFlat && isDragTarget && hoverPosition.position === index
								}
								onDragStart={() => setDraggedMR(mr)}
								onDragEnd={() => {
									setDraggedMR(null);
									setHoverPosition(null);
								}}
								onDragOverTop={
									!isFlat && acceptsDraggedMR && !isBeingDragged
										? () =>
												setHoverPosition(
													wouldMove(
														column.id,
														swimlane.id,
														persistedPositionAt(index),
													)
														? {
																column: column.id,
																swimlane: swimlane.id,
																position: index,
															}
														: null,
												)
										: undefined
								}
								onDragOverBottom={
									!isFlat && acceptsDraggedMR && !isBeingDragged
										? () =>
												setHoverPosition(
													wouldMove(
														column.id,
														swimlane.id,
														persistedPositionAt(index + 1),
													)
														? {
																column: column.id,
																swimlane: swimlane.id,
																position: index + 1,
															}
														: null,
												)
										: undefined
								}
								onDropTop={
									!isFlat && acceptsDraggedMR && !isBeingDragged
										? (event) => {
												event.stopPropagation();
												handleDrop(
													event,
													column.id,
													swimlane.id,
													persistedPositionAt(index),
												);
											}
										: undefined
								}
								onDropBottom={
									!isFlat && acceptsDraggedMR && !isBeingDragged
										? (event) => {
												event.stopPropagation();
												handleDrop(
													event,
													column.id,
													swimlane.id,
													persistedPositionAt(index + 1),
												);
											}
										: undefined
								}
								onDelete={
									column.id !== "deleted" && column.id !== "merged"
										? handleDeleteItem
										: undefined
								}
								onRestore={
									column.id === "deleted" && mr.state !== "closed"
										? handleRestoreItem
										: undefined
								}
								onMoveColumn={
									column.id !== "merged" ? handleMoveColumn : undefined
								}
								moveColumns={
									mr.state === "closed"
										? moveColumns.filter((item) => item.id !== "new")
										: moveColumns
								}
								onMoveSwimlane={isFlat ? undefined : handleMoveSwimlane}
								moveSwimlanes={moveSwimlanes}
								laneId={column.id}
								enableCommentsExpansion={enableCommentsExpansion}
							/>
						</Fragment>
					);
				})}
				{isDragTarget && hoverPosition.position === mrs.length && (
					<li className="mx-2 mb-2 h-1 list-none rounded-full bg-[var(--color-accent)]" />
				)}
			</ul>
		);
	};

	if (loading) {
		return (
			<div className="w-full p-4">
				<div className="w-full">
					<div className="mb-3 flex gap-3">
						{visibleColumns.map((column) => (
							<div
								key={column.id}
								className={`${column.color} min-w-0 flex-1 rounded-lg px-4 py-3 font-semibold text-white`}
							>
								{column.title}
							</div>
						))}
					</div>
					<div className="mb-2 h-10 animate-pulse rounded-lg bg-[var(--color-bg-primary)]" />
					<div className="flex items-stretch gap-3">
						{visibleColumns.map((column) => (
							<div
								key={column.id}
								className="h-32 min-w-0 flex-1 animate-pulse rounded-lg bg-[var(--color-bg-primary)]"
							/>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			{error && (
				<div className="mb-4 rounded-lg border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-4 py-3 text-[var(--color-error-text)] shadow-sm">
					<p className="font-medium">Error loading data</p>
					<p className="text-sm">{error}</p>
				</div>
			)}

			<div className="flex items-center justify-end gap-3 px-4 pt-2">
				{workstreamsEnabled &&
					(isAddingSwimlane ? (
						<form
							className="flex items-center gap-2"
							onSubmit={handleAddSwimlane}
						>
							<label htmlFor="new-swimlane-name" className="sr-only">
								Work stream name
							</label>
							<input
								id="new-swimlane-name"
								ref={newSwimlaneInputRef}
								value={newSwimlaneName}
								onChange={(event) => setNewSwimlaneName(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Escape") {
										setNewSwimlaneName("");
										setIsAddingSwimlane(false);
									}
								}}
								placeholder="Work stream name"
								maxLength={80}
								className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							/>
							<button
								type="submit"
								disabled={!newSwimlaneName.trim()}
								className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-[var(--color-accent-contrast)] disabled:opacity-50"
							>
								Create swimlane
							</button>
							<button
								type="button"
								onClick={() => {
									setNewSwimlaneName("");
									setIsAddingSwimlane(false);
								}}
								className="rounded-md p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
								aria-label="Cancel creating swimlane"
							>
								<X className="h-4 w-4" />
							</button>
						</form>
					) : (
						<button
							type="button"
							onClick={() => setIsAddingSwimlane(true)}
							className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
							title="Create a horizontal swimlane for a work stream"
						>
							<Plus className="h-4 w-4" /> Add swimlane
						</button>
					))}
			</div>

			<div className="w-full p-4">
				<div
					className="sticky top-0 z-20 mb-3 grid gap-3 bg-[var(--color-bg-app)] pb-1"
					style={{
						gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(0, 1fr))`,
					}}
				>
					{visibleColumns.map((column) => (
						<div
							key={column.id}
							className={`${column.color} min-w-0 rounded-lg px-4 py-3 text-white shadow-sm`}
						>
							<h2 className="font-semibold">{column.title}</h2>
						</div>
					))}
				</div>

				{workstreamsEnabled ? (
					<div
						className="grid items-stretch gap-3"
						style={{
							gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(0, 1fr))`,
						}}
					>
						{renderColumnCell(
							swimlanes.find((swimlane) => swimlane.id === DEFAULT_SWIMLANE_ID),
							COLUMN_DEFINITIONS.find((column) => column.id === "new"),
						)}

						<div className="min-w-0" style={{ gridColumn: "2 / -1" }}>
							{swimlanes.map((swimlane) => {
								const itemCount = workstreamColumns.reduce(
									(total, column) =>
										total + board[swimlane.id][column.id].length,
									0,
								);
								const collapsed = collapsedSwimlanes.has(swimlane.id);
								const isHeaderDragTarget =
									hoverPosition?.header === swimlane.id;
								const reorderDropEdge =
									swimlaneDropTarget?.id === swimlane.id &&
									draggedSwimlaneId !== swimlane.id
										? swimlaneDropTarget.edge
										: undefined;
								return (
									<section
										key={swimlane.id}
										className="relative mb-4"
										aria-label={`${swimlane.title} work stream`}
										onDragOver={(event) =>
											handleSwimlaneReorderDragOver(event, swimlane.id)
										}
										onDrop={(event) =>
											handleSwimlaneReorderDrop(event, swimlane.id)
										}
									>
										{reorderDropEdge === "before" && (
											<div className="absolute -top-1.5 left-0 right-0 z-30 h-1 rounded-full bg-[var(--color-accent)]" />
										)}
										{reorderDropEdge === "after" && (
											<div className="absolute -bottom-1.5 left-0 right-0 z-30 h-1 rounded-full bg-[var(--color-accent)]" />
										)}
										<SwimlaneHeader
											swimlane={swimlane}
											itemCount={itemCount}
											collapsed={collapsed}
											onToggle={handleToggleSwimlane}
											onRename={handleRenameSwimlane}
											onDelete={requestDeleteSwimlane}
											onReorderDragStart={handleSwimlaneReorderStart}
											onReorderDragEnd={handleSwimlaneReorderEnd}
											onDragOver={(event) =>
												handleSwimlaneTitleDragOver(event, swimlane.id)
											}
											onDrop={(event) =>
												handleSwimlaneTitleDrop(event, swimlane.id)
											}
											isDragTarget={isHeaderDragTarget}
										/>

										{!collapsed && (
											<div className="flex items-stretch gap-3">
												{workstreamColumns.map((column) =>
													renderColumnCell(swimlane, column),
												)}
											</div>
										)}
									</section>
								);
							})}
						</div>
					</div>
				) : (
					<div className="flex items-stretch gap-3">
						{visibleColumns.map((column) =>
							renderColumnCell(
								FLAT_SWIMLANE,
								column,
								flatBoard[column.id],
								true,
							),
						)}
					</div>
				)}
			</div>
			{swimlanePendingDelete && (
				<ConfirmationDialog
					title={`Delete “${swimlanePendingDelete.title}”?`}
					description="Its merge requests will move to Anything Else. This action cannot be undone."
					confirmLabel="Delete swimlane"
					onConfirm={confirmDeleteSwimlane}
					onCancel={cancelDeleteSwimlane}
				/>
			)}
		</>
	);
};
